import './bootstrap.js'
import { createApp, nextTick } from 'vue'
import { createPinia } from 'pinia'
import { createPersistedState } from 'pinia-plugin-persistedstate'
import { createInertiaApp, Link } from '@inertiajs/vue3'
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import Dropdown from 'bootstrap/js/dist/dropdown.js'
import Modal from 'bootstrap/js/dist/modal.js'
import Collapse from  'bootstrap/js/dist/collapse.js'
import { globalMixins } from '@/Mixins/globalMixins.js'
import Notifications from '@kyvg/vue3-notification'
import i18n from '@/i18n/i18n.js';
import { init as initSentry, captureConsoleIntegration } from "@sentry/vue"
import { useUserStore } from '@/stores/useUserStore.js'
import { useChannelStore } from '@/stores/useChannelStore.js'
import { useNewFeedStore } from '@/stores/useNewFeedStore.js'
import { jam as jamSdk } from '@jam.dev/sdk'

// Merge SDK metadata onto existing Jam global (so recorder from CDN isn't lost)
if (typeof window !== 'undefined') {
    const existingJam = window.jam || {}
    if (!existingJam.metadata && jamSdk?.metadata) {
        existingJam.metadata = jamSdk.metadata
    }
    window.jam = existingJam
}

import { DesignSystem } from "@marketscale/studio-design-system";
import "@marketscale/studio-design-system/dist/studio-design-system.css";
import { VueQueryPlugin } from '@tanstack/vue-query'
import { queryClient } from '@/api/queryClient'
import { VueQueryDevtools } from '@tanstack/vue-query-devtools'

createInertiaApp({
    resolve: (name) => resolvePageComponent(`./Pages/${name}.vue`, import.meta.glob('./Pages/**/*.vue')),
    setup({ el, App, props, plugin }) {
        const pinia = createPinia()
        pinia.use(createPersistedState({
            storage: localStorage,
            // Only persist feed data, not loading states
            serializer: {
                deserialize: JSON.parse,
                serialize: JSON.stringify,
            },
        }))

        const appInstance = createApp(App, props)
            .use(plugin)
            .use(DesignSystem)
            .use(pinia)
            .use(VueQueryPlugin, { queryClient })
            .component('inertia-link', Link)
            .component('VueQueryDevtools', VueQueryDevtools)
            .mixin({ methods: { route, ...globalMixins } })
            .use(Notifications)

        initSentry({
            app: appInstance,
            dsn: "https://33238e13685cb2653b30eaeb691cc29b@o4507346095636480.ingest.us.sentry.io/4507346986532864",  // NOTES: maybe it should be replaced with something like this (dsn: import.meta.env.VITE_SENTRY_DSN,)
            integrations: [
                // browserTracingIntegration(),  // NOTES: maybe something else needs to be added here, as it was in Vue2 (Sentry.browserTracingIntegration({ InertiaApp }),)
                // replayIntegration(),
                captureConsoleIntegration({ levels: ['error'] }),
            ],
            environment: props?.initialPage?.props?.appEnv,  // NOTES: perhaps such cases on the project should be changed to import.meta.env.VITE_APP_ENV
            enabled: ( props?.initialPage?.props?.appEnv !== 'local' ) && ( props?.initialPage?.props?.appEnv !== 'sandbox' ),
            tracesSampleRate: 0.05,
            tracePropagationTargets: ["localhost", /^https:\/\/(sandbox|staging)\.studio\.marketscale\.com\/api/],
            replaysSessionSampleRate: 0.05,
            replaysOnErrorSampleRate: 1.0,
            ignoreErrors: [
                // Outlook Safe Links scanner pre-fetches links and triggers JS errors with this signature
                /Object Not Found Matching Id:\d+, MethodName:/,
                // Benign Chrome notification — fires when ResizeObserver delivers a result late
                /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/,
                // Post-deploy: stale clients try to load now-missing chunks. User sees a reload prompt.
                /Loading chunk \d+ failed/,
                'ChunkLoadError',
                /Failed to fetch dynamically imported module/,
                /Unable to preload CSS/,
                // Older / non-standard browsers and headless scrapers
                'SharedWorker is not defined',
            ],
        })

        appInstance.config.globalProperties.$dropdown = Dropdown
        appInstance.config.globalProperties.$modal = Modal
        appInstance.config.globalProperties.$collapse = Collapse

        // Explicitly add $notify to global properties for consistent access
        appInstance.config.globalProperties.$notify = appInstance.config.globalProperties.$notify || function(options) {
          console.warn('$notify not available, falling back to console')
        }
        appInstance.config.warnHandler = (msg, vm, trace) => {
            if (msg.includes('hydration')) {
                console.warn('Hydration Warning:', msg, vm, trace)
            } else {
                console.warn('WarnHandler: ', msg, vm, trace)
            }
        }
        appInstance.use(i18n);

        try {
            appInstance.mount(el)
        } catch (error) {
            console.error("=== APP.JS: Error mounting app:", error)
            throw error
        }

        const registerJamMetadata = (userStore) => {
            if (typeof window === 'undefined') return
            const jamGlobal = window.jam
            if (!jamGlobal?.metadata) return

            const user = userStore.currentUser
            const permissions = userStore.userPermissions || userStore.permissions || []
            const channel = userStore.currentChannel
            const role = userStore.currentRole

            jamGlobal.metadata(() => {
                const metaData = {
                    timeSincePageLoadMs: performance.now(),
                    isAuthenticated: !!user,
                }

                if (user) {
                    metaData.userId = user?.id ?? null
                    metaData.userEmail = user?.email ?? null
                    metaData.userName = user?.name ?? ((`${user?.f_name ?? ''} ${user?.l_name ?? ''}`).trim() || user?.email || null)
                    metaData.channelId = channel?.id ?? user?.current_organization_id ?? null
                    metaData.channelName = channel?.name ?? null
                    metaData.role = role ?? null
                    metaData.permissions = permissions
                }

                return metaData
            })
        }

        nextTick(async () => {
            try {
                const userStore = useUserStore()
                const channelStore = useChannelStore()

                await userStore.init()
                await channelStore.init(props.initialPage.props)

                // Make Pinia stores accessible in browser console for debugging
                if (import.meta.env.DEV) {
                    window.__PINIA_STORES__ = {
                        userStore,
                        channelStore,
                        feedStore: useNewFeedStore()
                    }

                    // TanStack Query DevTools - mount as floating panel
                    // Access queryClient via: window.__TANSTACK_QUERY_CLIENT__
                    window.__TANSTACK_QUERY_CLIENT__ = queryClient
                }

                registerJamMetadata(userStore)
            } catch (error) {
                console.error("=== APP.JS: Error initializing stores:", error)
            }
        })
    },
}).then(() => {
    document.getElementById('app').removeAttribute('data-page')
})
